import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '@rydres/api-client';
import { BRAND, addMonthsYmd, describeStatus, localParts, monthRange, yearRange } from '@rydres/shared';
import { Badge, Button, Card, Field, Input, Notice, QrCode, TextArea, fmtDate, fmtDateTime, fmtPhone, formatUsd, toE164 } from '@rydres/ui';
import { chatApi, driverApi } from '../lib/api.js';
import { lastLine, useOpenChat, when } from '../lib/chat.js';
import { useProfile } from '../lib/profile.js';
import type { ConversationSummary, Passenger, PassengerDetail, RiderRevenue } from '../lib/types.js';
import { ContactButtons, EarlierConversations, UnreadCount } from './Conversation.js';

const errorText = (err: unknown, fallback: string) => (err instanceof ApiError ? err.message : fallback);

/** The People tab's Passengers list: everyone who booked, everyone added by hand or by QR, with Message and Call. */
export function PassengersPanel({ convos }: { convos: ConversationSummary[] | null }) {
  const { profile } = useProfile();
  const [data, setData] = useState<{ passengers: Passenger[]; joinUrl: string | null; accountsOpen: boolean } | null>(null);
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  const load = () => driverApi.passengers().then(setData, () => setData({ passengers: [], joinUrl: null, accountsOpen: false }));
  useEffect(() => void load(), []);
  const needle = q.trim().toLowerCase();
  const digits = needle.replace(/\D/g, '');
  const shown = data?.passengers.filter((p) => !needle || (p.name ?? '').toLowerCase().includes(needle) || (digits.length >= 3 && p.phone.includes(digits))) ?? [];
  const withRiders = convos?.filter((c) => c.kind === 'passenger') ?? [];
  const byLink = new Map(withRiders.map((c) => [c.passengerLinkId, c]));
  const listed = new Set(data?.passengers.map((p) => p.id));
  const earlier = data ? withRiders.filter((c) => (!c.passengerLinkId || !listed.has(c.passengerLinkId)) && (!needle || c.name.toLowerCase().includes(needle))) : [];
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or phone" />
        <Button onClick={() => setAdding(true)} className="shrink-0">+ Add</Button>
      </div>
      {data === null ? (
        <p className="px-1 text-sm text-muted">Loading…</p>
      ) : data.passengers.length === 0 ? (
        <p className="px-1 text-sm text-muted">Everyone who books you lands here, one entry per phone number. You can also add passengers yourself.</p>
      ) : (
        <div className="space-y-2">
          {shown.map((p) => {
            const c = byLink.get(p.id) ?? null;
            const name = p.name ?? fmtPhone(p.phone);
            return (
              <div key={p.id} className="rounded-2xl border border-line bg-card">
                <Link to={`/people/passengers/${p.id}`} className="flex items-center justify-between gap-3 px-4 pb-2 pt-4">
                  <div className="min-w-0">
                    <div className={`flex items-center gap-2 truncate ${c?.unread ? 'font-black' : 'font-bold'}`}>{name}{p.shared && <Badge value="shared" tone="ok" />}{p.blocked && <Badge value="blocked" tone="danger" />}</div>
                    <div className="truncate text-xs text-muted">{fmtPhone(p.phone)}{p.lastRideAt ? ` · ${Date.parse(p.lastRideAt) > Date.now() ? 'next ride' : 'last ride'} ${fmtDate(p.lastRideAt, profile?.timezone)}` : ''}</div>
                    {c?.last && <div className="truncate text-sm text-ink2">{when(c.last.at)} · {lastLine(c)}</div>}
                  </div>
                  {c && c.unread > 0 ? <UnreadCount n={c.unread} /> : <span className="text-muted" aria-hidden="true">›</span>}
                </Link>
                <div className="px-4 pb-3">
                  <ContactButtons name={name} phone={p.phone} conversationId={c?.id ?? null} canMessage={p.canMessage} open={() => chatApi.open({ passengerId: p.id })} />
                </div>
              </div>
            );
          })}
          {shown.length === 0 && <p className="px-1 text-sm text-muted">No one matches.</p>}
        </div>
      )}
      <EarlierConversations list={earlier} />
      {adding && data && <AddPassenger joinUrl={data.accountsOpen ? data.joinUrl : null} onDone={() => { setAdding(false); void load(); }} onCancel={() => setAdding(false)} />}
    </div>
  );
}

/** Add by QR (the passenger scans and shares their own details) or by hand. */
function AddPassenger({ joinUrl, onDone, onCancel }: { joinUrl: string | null; onDone: () => void; onCancel: () => void }) {
  const navigate = useNavigate();
  const [how, setHow] = useState<'qr' | 'hand'>(joinUrl ? 'qr' : 'hand');
  const [f, setF] = useState({ name: '', phone: '', email: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const phone = toE164(f.phone);
    if (!phone) return setError('That phone number does not look right.');
    setBusy(true);
    setError(null);
    try {
      const p = await driverApi.addPassenger({ name: f.name.trim(), phone, email: f.email.trim() || null });
      onDone();
      navigate(`/people/passengers/${p.id}`);
    } catch (err) {
      setError(errorText(err, 'Could not add them.'));
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-scrim p-4 sm:items-center" onClick={onCancel}>
      <div className="max-h-full w-full max-w-md space-y-3 overflow-y-auto rounded-3xl border border-line bg-card p-5" onClick={(e) => e.stopPropagation()}>
        <div className="text-lg font-black">Add a passenger</div>
        {joinUrl && (
          <div className="flex gap-1 rounded-full bg-card2 p-1 text-sm font-bold">
            {(['qr', 'hand'] as const).map((h) => (
              <button key={h} onClick={() => setHow(h)} className={`flex-1 rounded-full px-3 py-1.5 ${how === h ? 'bg-card shadow-card' : 'text-muted'}`}>{h === 'qr' ? 'QR code' : 'By hand'}</button>
            ))}
          </div>
        )}
        {how === 'qr' && joinUrl ? (
          <div className="space-y-3 text-center">
            <QrCode value={joinUrl} alt="QR code to share their details with you" className="mx-auto h-56 w-56 rounded-xl bg-white p-2" />
            <p className="text-sm text-ink2">Ask them to point their phone camera at the code. They confirm their number with a text and share their details with you; anyone who already has a {BRAND.name} account just taps Share.</p>
            <p className="break-all text-xs text-muted">{joinUrl}</p>
            <Button variant="ghost" className="w-full" onClick={onDone}>Done</Button>
          </div>
        ) : (
          <form onSubmit={(e) => void submit(e)} className="space-y-3">
            <Field label="Name"><Input required autoFocus value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
            <Field label="Mobile number" hint="One entry per number: someone already on your list is not added twice"><Input required inputMode="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Field>
            <Field label="Email (optional)"><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
            {!joinUrl && <p className="text-xs text-muted">Passengers will soon be able to add themselves by scanning your QR code.</p>}
            {error && <Notice tone="danger">{error}</Notice>}
            <div className="flex gap-2">
              <Button variant="ghost" className="flex-1" onClick={onCancel}>Cancel</Button>
              <Button type="submit" className="flex-1" disabled={busy}>{busy ? 'Adding…' : 'Add'}</Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/** What this rider has brought in, by month or year, with the rides behind it and exports. */
function RevenueCard({ id, tz }: { id: string; tz?: string }) {
  const today = localParts(new Date(), tz ?? Intl.DateTimeFormat().resolvedOptions().timeZone).ymd;
  const [mode, setMode] = useState<'month' | 'year'>('month');
  const [anchor, setAnchor] = useState(today);
  const [data, setData] = useState<RiderRevenue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const period = mode === 'month' ? monthRange(anchor) : yearRange(anchor);
  useEffect(() => {
    setData(null);
    driverApi.revenue(id, period).then(setData, (err) => setError(errorText(err, 'Could not load the revenue.')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, period.from, period.to]);
  const download = async (format: 'pdf' | 'csv') => {
    setError(null);
    // The tab has to open during the tap, or a pop-up blocker stops it; it goes to the file once the link is ready.
    const tab = window.open('', '_blank');
    try {
      const { url } = await driverApi.revenueLink(id, { ...period, format });
      if (tab) tab.location.href = url;
      else window.location.href = url;
    } catch (err) {
      tab?.close();
      setError(errorText(err, 'Could not make the report.'));
    }
  };
  const show = (opts: Intl.DateTimeFormatOptions) => new Date(`${anchor}T12:00:00Z`).toLocaleDateString('en-US', { timeZone: 'UTC', ...opts });
  return (
    <Card title="Revenue from this rider" info="Fares collected less refunds, including what you keep under your cancellation and no-show terms. Stripe's own fee is not included; your Stripe dashboard has it. Trips logged with no phone match no rider, so they are not counted here.">
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <button onClick={() => setAnchor(addMonthsYmd(anchor, mode === 'month' ? -1 : -12))} aria-label={`Earlier ${mode}`} className="h-10 w-10 rounded-full bg-card2 font-bold">‹</button>
          <div className="text-center font-black">{mode === 'month' ? show({ month: 'long', year: 'numeric' }) : anchor.slice(0, 4)}</div>
          <button onClick={() => setAnchor(addMonthsYmd(anchor, mode === 'month' ? 1 : 12))} aria-label={`Later ${mode}`} className="h-10 w-10 rounded-full bg-card2 font-bold">›</button>
        </div>
        <div className="flex gap-1 rounded-full bg-card2 p-1 text-sm font-bold">
          {(['month', 'year'] as const).map((m) => (
            <button key={m} onClick={() => setMode(m)} aria-pressed={mode === m} className={`flex-1 rounded-full px-3 py-1.5 capitalize ${mode === m ? 'bg-card shadow-card' : 'text-muted'}`}>{m}</button>
          ))}
        </div>
        {error && <Notice tone="danger">{error}</Notice>}
        {data === null ? (
          !error && <p className="text-sm text-muted">Loading…</p>
        ) : (
          <>
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm text-ink2">Earned · {data.rides.length} {data.rides.length === 1 ? 'ride' : 'rides'}</span>
              <span className="text-2xl font-black">{formatUsd(data.totals.paidCents)}</span>
            </div>
            <dl className="space-y-1 text-sm">
              {data.totals.owedCents > 0 && <div className="flex justify-between gap-3"><dt className="text-ink2">Still owed</dt><dd className="font-bold">{formatUsd(data.totals.owedCents)}</dd></div>}
              {data.totals.refundedCents > 0 && <div className="flex justify-between gap-3"><dt className="text-ink2">Refunded</dt><dd className="font-bold">{formatUsd(data.totals.refundedCents)}</dd></div>}
              {data.totals.feeCents > 0 && <div className="flex justify-between gap-3"><dt className="text-ink2">{BRAND.name} fees on card payments</dt><dd className="font-bold">{formatUsd(data.totals.feeCents)}</dd></div>}
              {mode === 'year' && data.totals.byMonth.map((b) => (
                <div key={b.key} className="flex justify-between gap-3"><dt className="text-ink2">{new Date(`${b.key}-15T12:00:00Z`).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'long' })}</dt><dd className="font-bold">{formatUsd(b.paidCents)}</dd></div>
              ))}
            </dl>
            {data.rides.length > 0 && (
              <>
                <div className="flex gap-2">
                  <Button small variant="ghost" className="flex-1" onClick={() => void download('pdf')}>Report (PDF)</Button>
                  <Button small variant="ghost" className="flex-1" onClick={() => void download('csv')}>Spreadsheet (CSV)</Button>
                </div>
                <div className="space-y-2">
                  {data.rides.map((r) => (
                    <Link key={r.id} to={`/bookings/${r.id}`} className="flex items-start justify-between gap-3 rounded-xl bg-card2 p-3">
                      <div className="min-w-0">
                        <div className="text-sm font-bold">{fmtDate(r.date, tz)} · {r.rideStatus}</div>
                        <div className="truncate text-xs text-muted">{r.pickupAddress} → {r.dropoffAddress}</div>
                        <div className="text-xs text-muted">{r.label}</div>
                      </div>
                      <div className="text-sm font-black">{formatUsd(r.paidCents)}</div>
                    </Link>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

/** One passenger: their own details when shared, this driver's notes, and the rides together. */
export function PassengerScreen() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const openChat = useOpenChat();
  const { profile } = useProfile();
  const [p, setP] = useState<PassengerDetail | null>(null);
  const [mine, setMine] = useState({ name: '', email: '', notes: '' });
  const [msg, setMsg] = useState<{ tone: 'ok' | 'danger'; text: string } | null>(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    driverApi.passenger(id).then(
      (x) => {
        setP(x);
        setMine({ name: x.mine.name ?? '', email: x.mine.email ?? '', notes: x.mine.notes ?? '' });
      },
      () => navigate('/people?tab=passengers'),
    );
  }, [id, navigate]);
  if (!p) return <p className="text-sm text-muted">Loading…</p>;
  const tz = profile?.timezone;
  const ask = p.askUrl ? `Hi${p.name ? ` ${p.name.split(' ')[0]}` : ''}, this is ${profile?.displayName ?? 'your driver'}. Please add your details for your rides with me here: ${p.askUrl}` : null;
  const save = async (e: FormEvent) => {
    e.preventDefault();
    setMsg(null);
    try {
      setP(await driverApi.editPassenger(id, { name: mine.name.trim() || null, email: mine.email.trim() || null, notes: mine.notes.trim() || null }));
      setMsg({ tone: 'ok', text: 'Saved.' });
    } catch (err) {
      setMsg({ tone: 'danger', text: errorText(err, 'Could not save.') });
    }
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/people?tab=passengers')} className="rounded-full bg-card2 p-2 px-3" aria-label="Back">‹</button>
        <h1 className="min-w-0 truncate text-xl font-black">{p.name ?? fmtPhone(p.phone)}</h1>
        {p.shared && <Badge value="shared" tone="ok" />}
        {p.blocked && <Badge value="requests stopped" tone="danger" />}
      </div>
      <div className="flex flex-wrap gap-2">
        {p.canMessage && (
          <button
            type="button"
            onClick={() => void chatApi.open({ passengerId: p.id }).then((c) => openChat(c.id), (err) => setMsg({ tone: 'danger', text: err instanceof ApiError ? err.message : 'Could not open the conversation.' }))}
            className="rounded-xl bg-card2 px-3 py-2 text-sm font-bold"
          >
            Message
          </button>
        )}
        <a href={`tel:${p.phone}`} className="rounded-xl bg-card2 px-3 py-2 text-sm font-bold">Call {fmtPhone(p.phone)}</a>
        <a href={`sms:${p.phone}`} className="rounded-xl bg-card2 px-3 py-2 text-sm font-bold">Text</a>
        <Link to="/log" className="rounded-xl bg-card2 px-3 py-2 text-sm font-bold">Log a trip</Link>
      </div>

      {p.theirs ? (
        <Card title="Their details (kept up to date by them)">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
            <dt className="text-muted">Name</dt><dd className="font-bold">{p.theirs.name ?? '—'}</dd>
            <dt className="text-muted">Email</dt><dd>{p.theirs.email ?? '—'}</dd>
            <dt className="text-muted">Home</dt><dd>{p.theirs.homeAddress ?? '—'}</dd>
            <dt className="text-muted">Needs</dt><dd>{p.theirs.needs ?? '—'}</dd>
          </dl>
        </Card>
      ) : ask ? (
        <Card title="Missing details?" info="Send them your link; they confirm their number and share their own details, which then stay up to date.">
          <div className="mt-3 flex flex-wrap gap-2">
            <a className="rounded-xl bg-brand px-3 py-2 text-sm font-bold text-on-brand brand-press" href={`sms:${p.phone}?&body=${encodeURIComponent(ask)}`}>Text them the link</a>
            <Button small variant="ghost" onClick={async () => { await navigator.clipboard?.writeText(ask).catch(() => undefined); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>{copied ? 'Copied' : 'Copy message'}</Button>
            {typeof navigator.share === 'function' && <Button small variant="ghost" onClick={() => void navigator.share({ text: ask }).catch(() => undefined)}>Share…</Button>}
          </div>
        </Card>
      ) : null}

      <form onSubmit={(e) => void save(e)}>
        <Card title="Your notes (only you see these)">
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Name you use"><Input value={mine.name} onChange={(e) => setMine({ ...mine, name: e.target.value })} /></Field>
              <Field label="Email"><Input type="email" value={mine.email} onChange={(e) => setMine({ ...mine, email: e.target.value })} /></Field>
            </div>
            <Field label="Private notes"><TextArea value={mine.notes} onChange={(e) => setMine({ ...mine, notes: e.target.value })} placeholder="Gate code, usual pickup spot, how they like the ride…" /></Field>
            {msg && <Notice tone={msg.tone}>{msg.text}</Notice>}
            <Button type="submit" small>Save notes</Button>
          </div>
        </Card>
      </form>

      <RevenueCard id={id} tz={tz} />

      <section className="space-y-2">
        <h2 className="px-1 text-xs font-bold uppercase tracking-wide text-muted">Rides with you</h2>
        {p.rides.length === 0 ? (
          <p className="px-1 text-sm text-muted">None yet.</p>
        ) : (
          p.rides.map((r) => (
            <Link key={r.id} to={`/bookings/${r.id}`} className="flex items-start justify-between gap-3 rounded-2xl border border-line bg-card p-3">
              <div className="min-w-0">
                <div className="text-sm font-bold">{fmtDateTime(r.startsAt, tz)}</div>
                <div className="truncate text-xs text-muted">{r.pickupAddress} → {r.dropoffAddress}</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-black">{r.priceCents != null ? formatUsd(r.priceCents) : '—'}</div>
                <Badge value={describeStatus(r).label} tone={describeStatus(r).tone} />
              </div>
            </Link>
          ))
        )}
      </section>
      <button
        onClick={() => void driverApi.blockPassenger(id, !p.blocked).then(setP, (err) => setMsg({ tone: 'danger', text: errorText(err, 'That did not work.') }))}
        className="block w-full text-center text-sm font-bold text-ink2"
      >
        {p.blocked ? 'Allow their ride requests again' : 'Stop their ride requests'}
      </button>
      <button
        onClick={async () => {
          await driverApi.removePassenger(id).catch(() => undefined);
          navigate('/people?tab=passengers');
        }}
        className="block w-full text-center text-sm text-danger"
      >
        Remove from my passengers
      </button>
    </div>
  );
}
